import { motion } from 'framer-motion'
import { useRef, useState, useEffect } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'

const screens = [
    {
        id: 'step1',
        title: 'Your digital closet',
        description: 'Start with what you own. Everything organized, categorized, and searchable.',
        image: '/screenshots/slide-1-wardrobe.png',
        alt: 'Wardrobe overview screen showing grid of clothes',
    },
    {
        id: 'step2',
        title: 'Deep details',
        description: 'Michela understands fabric, warmth, and formality for every piece.',
        image: '/screenshots/slide-2-item-detail.png',
        alt: 'Detail view of striped socks showing metadata',
    },
    {
        id: 'step3',
        title: 'Just ask',
        description: 'Tell Michela the plan. She considers the weather and the vibe.',
        image: '/screenshots/slide-3-prompt.png',
        alt: 'User typing prompt: Dinner with friends',
    },
    {
        id: 'step4',
        title: 'Context aware',
        description: 'Mixing textures and colors based on your specific request.',
        image: '/screenshots/slide-4-loading.png',
        alt: 'Loading screen showing mixing animation',
    },
    {
        id: 'step5',
        title: '3 Curated Options',
        description: 'Instant outfits that match your prompt and the weather.',
        image: '/screenshots/slide-5-results-grid.png',
        alt: 'Result screen showing 3 generated outfit options',
    },
    {
        id: 'step6',
        title: 'The "Why"',
        description: 'A breakdown of exactly why this outfit works for your occasion.',
        image: '/screenshots/slide-6-results-desc.png',
        alt: 'Description text explaining the outfit choice',
    },
    {
        id: 'step7',
        title: 'Try & Save',
        description: 'See it on you with Virtual Try-On, then save it to your Lookbook.',
        image: '/screenshots/slide-7-lookbook.png',
        alt: 'Lookbook screen showing virtual try-on result',
    },
]

export function SeeItInAction() {
    const scrollContainerRef = useRef<HTMLDivElement>(null)
    const [canScrollLeft, setCanScrollLeft] = useState(false)
    const [canScrollRight, setCanScrollRight] = useState(true)

    const checkScrollPosition = () => {
        const container = scrollContainerRef.current
        if (!container) return
        
        setCanScrollLeft(container.scrollLeft > 10)
        setCanScrollRight(
            container.scrollLeft < container.scrollWidth - container.clientWidth - 10
        )
    }

    useEffect(() => {
        const container = scrollContainerRef.current
        if (!container) return
        
        checkScrollPosition()
        container.addEventListener('scroll', checkScrollPosition)
        window.addEventListener('resize', checkScrollPosition)
        
        return () => {
            container.removeEventListener('scroll', checkScrollPosition)
            window.removeEventListener('resize', checkScrollPosition)
        }
    }, [])

    const scroll = (direction: 'left' | 'right') => {
        const container = scrollContainerRef.current
        if (!container) return
        
        const cardWidth = 300
        const gap = 24
        const scrollAmount = cardWidth + gap
        
        container.scrollBy({
            left: direction === 'left' ? -scrollAmount : scrollAmount,
            behavior: 'smooth'
        })
    }

    return (
        <section className="py-24 sm:py-32 bg-paper overflow-hidden relative">
            {/* Background decorative elements */}
            <div className="absolute inset-0 pointer-events-none overflow-hidden">
                <div className="absolute top-1/2 right-1/4 w-[500px] h-[500px] bg-accent-subtle/8 rounded-full blur-[100px] -translate-y-1/2" />
                <div className="absolute top-1/3 left-1/4 w-[400px] h-[400px] bg-accent-subtle/5 rounded-full blur-[80px]" />
            </div>

            <div className="max-w-7xl mx-auto px-8 sm:px-6 lg:px-8 relative z-10">
                {/* Section Header */}
                <div className="max-w-3xl mx-auto text-center mb-12 sm:mb-16 space-y-4">
                    <h2 className="font-display text-4xl sm:text-5xl font-semibold text-ink">
                        See Michela in action
                    </h2>
                    <p className="text-lg sm:text-xl text-ink-tertiary max-w-2xl mx-auto leading-relaxed px-6 sm:px-0">
                        From abstract idea to concrete outfit in seconds.
                    </p>
                </div>

                {/* Carousel container */}
                <div className="relative">
                    {/* Left arrow - dark bg, white icon */}
                    <button
                        onClick={() => scroll('left')}
                        className={`absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 z-30 w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-ink shadow-xl flex items-center justify-center transition-all duration-200 hover:bg-ink-secondary ${
                            canScrollLeft ? 'opacity-100' : 'opacity-0 pointer-events-none'
                        }`}
                        aria-label="Previous"
                    >
                        <ChevronLeft className="w-5 h-5 text-white" />
                    </button>

                    {/* Right arrow - dark bg, white icon */}
                    <button
                        onClick={() => scroll('right')}
                        className={`absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 z-30 w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-ink shadow-xl flex items-center justify-center transition-all duration-200 hover:bg-ink-secondary ${
                            canScrollRight ? 'opacity-100' : 'opacity-0 pointer-events-none'
                        }`}
                        aria-label="Next"
                    >
                        <ChevronRight className="w-5 h-5 text-white" />
                    </button>

                    {/* Gradient fades on edges - larger on right to signal more content */}
                    <div className="absolute left-0 top-0 bottom-0 w-12 z-20 pointer-events-none bg-gradient-to-r from-paper to-transparent" />
                    <div className="absolute right-0 top-0 bottom-0 w-48 sm:w-72 z-20 pointer-events-none">
                        {/* Layered gradients for smooth fade effect */}
                        <div className="absolute inset-0 bg-gradient-to-l from-paper via-paper/70 to-transparent" />
                        <div className="absolute inset-0 bg-gradient-to-l from-paper/80 via-transparent to-transparent" />
                        {/* Blur layers - stacked to create gradient blur effect */}
                        <div className="absolute right-0 top-0 bottom-0 w-1/4 backdrop-blur-sm" style={{ maskImage: 'linear-gradient(to left, black, transparent)', WebkitMaskImage: 'linear-gradient(to left, black, transparent)' }} />
                        <div className="absolute right-0 top-0 bottom-0 w-1/2 backdrop-blur-[1px]" style={{ maskImage: 'linear-gradient(to left, black 20%, transparent)', WebkitMaskImage: 'linear-gradient(to left, black 20%, transparent)' }} />
                    </div>

                    {/* Scrollable cards container */}
                    <div
                        ref={scrollContainerRef}
                        className="flex overflow-x-auto snap-x snap-mandatory gap-6 py-8 px-12 scrollbar-hide scroll-smooth"
                    >
                        {screens.map((screen, index) => (
                            <motion.div
                                key={screen.id}
                                className="flex-shrink-0 w-[280px] sm:w-[300px] snap-center"
                            >
                                <motion.div
                                    whileHover={{ 
                                        y: -8,
                                        scale: 1.02,
                                    }}
                                    transition={{
                                        type: "spring",
                                        stiffness: 400,
                                        damping: 30
                                    }}
                                    className="relative rounded-[2rem] sm:rounded-[2.5rem] overflow-hidden shadow-card border border-paper-tertiary bg-paper"
                                >
                                    {/* Glass reflection effect */}
                                    <div className="absolute inset-0 bg-gradient-to-tr from-white/10 to-transparent pointer-events-none z-10" />
                                    
                                    <img
                                        src={screen.image}
                                        alt={screen.alt}
                                        className="w-full h-auto block"
                                        loading="lazy"
                                    />
                                    
                                    {/* Subtle inner shadow */}
                                    <div className="absolute inset-0 shadow-[inset_0_0_40px_rgba(0,0,0,0.02)] pointer-events-none" />
                                </motion.div>

                                <div className="mt-6 space-y-2 text-center">
                                    <div className="flex items-center justify-center gap-2.5">
                                        <span className="flex items-center justify-center w-6 h-6 rounded-full bg-ink text-white text-[11px] font-bold">
                                            {index + 1}
                                        </span>
                                        <h3 className="font-display text-lg font-semibold text-ink">
                                            {screen.title}
                                        </h3>
                                    </div>
                                    <p className="text-ink-tertiary text-sm leading-relaxed max-w-[260px] mx-auto">
                                        {screen.description}
                                    </p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    )
}
